import { CONNECT_RENTAL_INR, ConnectPlanPay } from '@components/onboarding/ConnectPlanPay';
import { useFbSdk } from '@hooks/useFbSdk';
import { useToast } from '@hooks/useToast';
import { isApiError } from '@services/auth.service';
import { getFbConfig } from '@services/onboarding.service';
import { createProject } from '@services/projects.service';
import { getWalletStatus } from '@services/wallet.service';
import { useProjectStore } from '@stores/project.store';
import { Button, Input, Modal } from '@ui/index';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export interface AddNumberModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Adds a WhatsApp number. Provisioning costs a ₹999/month rental that is
 * debited from the wallet on creation, so we make sure the wallet is funded
 * *before* running Meta's embedded signup:
 *
 *   - balance ≥ ₹999 → straight to FB.login → POST /projects (debits ₹999).
 *   - balance < ₹999 → pay ₹1499 (₹999 rental + ₹500 message credit) first,
 *     then FB.login → POST /projects.
 *
 * Pre-checking avoids burning a single-use FB OAuth code on a createProject
 * call that would only fail with 402. The 402 path is kept as a race fallback.
 */
export function AddNumberModal({ isOpen, onClose }: AddNumberModalProps): JSX.Element {
  const toast = useToast();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const setSelected = useProjectStore((s) => s.setSelected);

  const [displayName, setDisplayName] = useState('');
  const [mode, setMode] = useState<'form' | 'pay'>('form');
  const [connecting, setConnecting] = useState(false);

  // Static FB config is needed before FB.login; fetch it once the modal opens.
  const { data: fbConfig } = useQuery({
    queryKey: ['onboarding', 'fb-config'],
    queryFn: getFbConfig,
    enabled: isOpen,
    staleTime: 60 * 60 * 1000,
  });
  const { isReady: fbReady, login } = useFbSdk(fbConfig?.app_id ?? null);

  useEffect(() => {
    if (isOpen) {
      setDisplayName('');
      setMode('form');
    }
  }, [isOpen]);

  const create = useMutation({
    mutationFn: createProject,
    onSuccess: (project) => {
      toast.success({ title: 'WhatsApp number created' });
      setSelected(project.id);
      void qc.invalidateQueries({ queryKey: ['projects', 'list'] });
      onClose();
      navigate(`/projects/${project.id}`);
    },
    onError: (err) => {
      // Race fallback: the wallet dropped below the rental between the gate
      // check and the debit. Show the pay screen; paying re-runs the popup.
      if (isApiError(err) && err.statusCode === 402) {
        setMode('pay');
        return;
      }
      toast.error({
        title: 'Could not create project',
        message: isApiError(err) ? err.message : 'Please try again.',
      });
    },
  });

  const trimmed = displayName.trim();
  const valid = trimmed.length >= 1;
  const busy = connecting || create.isPending;

  // Run Meta's embedded signup, then create the project (debits the rental).
  const runCreate = async (): Promise<void> => {
    if (!fbConfig || !fbReady) {
      toast.error({ title: 'Still loading Facebook setup', message: 'Try again in a moment.' });
      return;
    }
    setConnecting(true);
    try {
      const { code, waba_id, phone_number_id } = await login({
        app_id: fbConfig.app_id,
        config_id: fbConfig.config_id,
        scope: fbConfig.scope,
      });
      create.mutate({ display_name: trimmed, fb_code: code, waba_id, phone_number_id });
    } catch (err) {
      const e = err as { code?: string; message?: string };
      if (e.code === 'USER_CANCEL') {
        toast.info({ title: 'You can try again anytime' });
      } else {
        toast.error({
          title: 'Facebook verification failed',
          message: e.message ?? 'Please try again.',
        });
      }
    } finally {
      setConnecting(false);
    }
  };

  const submit = async (): Promise<void> => {
    if (!valid || busy) return;
    if (!fbConfig || !fbReady) {
      toast.error({ title: 'Still loading Facebook setup', message: 'Try again in a moment.' });
      return;
    }
    try {
      const { balance } = await getWalletStatus();
      if (balance >= CONNECT_RENTAL_INR) {
        void runCreate();
      } else {
        setMode('pay');
      }
    } catch {
      // Can't read the balance — take the safe path and collect payment.
      setMode('pay');
    }
  };

  const onPaid = (): void => {
    setMode('form');
    void runCreate();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        if (!busy) onClose();
      }}
      title="Add WhatsApp Number"
      description="Verify a new WhatsApp Business number with Meta to add it to this workspace."
      size="md"
      accent="green"
    >
      {mode === 'pay' ? (
        <ConnectPlanPay onPaid={onPaid} />
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
          className="space-y-4"
        >
          <Input
            label="Display name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="e.g. Acme Support"
            autoFocus
            maxLength={100}
            disabled={busy}
          />

          <Button
            type="submit"
            variant="primary"
            className="w-full"
            disabled={!valid}
            loading={busy}
          >
            {connecting ? 'Opening Facebook…' : 'Verify with Meta & create'}
          </Button>

          <p className="text-center text-[12px] text-apple-gray-500">
            ₹999/month rental is debited from your wallet on creation. If your balance is
            low, you'll top up ₹1,499 (₹999 rental + ₹500 message credit) first.
          </p>
        </form>
      )}
    </Modal>
  );
}
